import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import logo from '../assets/logo.png';
import { useAuth } from '../context/AuthContext';
import { ArrowPathIcon, MagnifyingGlassIcon, ChatBubbleBottomCenterTextIcon, EnvelopeIcon } from '@heroicons/react/24/solid';
import api from '../utils/api';

// Debounce utility para la búsqueda
const debounce = (func, delay) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => func(...args), delay);
  };
};

// Normalizar texto para búsqueda (eliminando acentos y convirtiendo a mayúsculas)
const normalizeText = (text) => {
  if (!text) return '';
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .trim();
};

const TramiteList = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth() || { user: null, loading: true };
  const [days, setDays] = useState([]);
  const [pendingDays, setPendingDays] = useState([]); // Días con trámites pendientes
  const [selectedDays, setSelectedDays] = useState([]); // Días seleccionados (múltiple)
  const [tramites, setTramites] = useState([]);
  const [filteredTramites, setFilteredTramites] = useState([]);
  const [matrizadores, setMatrizadores] = useState([]);
  const [matrizadoresData, setMatrizadoresData] = useState([]);
  const [selectedMatrizador, setSelectedMatrizador] = useState('');
  const [matrizadorSearch, setMatrizadorSearch] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSendingEmails, setIsSendingEmails] = useState(false); // Estado para el envío de correos
  const [error, setError] = useState(null);
  const [totalTramites, setTotalTramites] = useState(0);
  const [showEmailConfirmation, setShowEmailConfirmation] = useState(false); // Estado para la confirmación de envío de correos
  const [emailRecipients, setEmailRecipients] = useState([]); // Matrizadores a notificar

  // Cargar datos de matrizadores para obtener los números de teléfono y correos
  useEffect(() => {
    const fetchMatrizadores = async () => {
      try {
        const response = await api.get('/api/audit/matrizadores/list/');
        if (Array.isArray(response.data)) {
          setMatrizadoresData(response.data);
        } else {
          toast.error('Error al cargar los datos de los matrizadores: formato de respuesta inválido.', {
            position: 'top-right',
            autoClose: 5000,
          });
          setMatrizadoresData([]);
        }
      } catch (err) {
        toast.error('Error al cargar los datos de los matrizadores.', {
          position: 'top-right',
          autoClose: 5000,
        });
        setMatrizadoresData([]);
      }
    };
    if (!loading && user) {
      fetchMatrizadores();
    }
  }, [user, loading]);

  // Determinar si el trámite es justificable (para distinguir visualmente, pero el semáforo se muestra siempre)
  const shouldShowSemaforo = (tramite) => {
    const hasComprobanteAfectado = tramite.comprobante_afectado && typeof tramite.comprobante_afectado === 'string' && tramite.comprobante_afectado.trim() !== '';
    return !(tramite.tipo === 'egreso' || hasComprobanteAfectado);
  };

  // Filtrado de trámites por matrizador y búsqueda
  const filteredTramitesMemo = useMemo(() => {
    let filtered = tramites || [];

    // Filtro por matrizador seleccionado
    if (selectedMatrizador) {
      filtered = filtered.filter((t) => normalizeText(t.usuario_matrizador) === normalizeText(selectedMatrizador));
    }

    // Filtro por búsqueda de matrizador
    if (matrizadorSearch) {
      filtered = filtered.filter((t) => normalizeText(t.usuario_matrizador || '').includes(normalizeText(matrizadorSearch)));
    }

    // Filtro por búsqueda avanzada (código o descripción)
    if (searchQuery) {
      filtered = filtered.filter((tramite) => {
        const matchesNoTemporal = normalizeText(tramite.no_temporal || '').includes(normalizeText(searchQuery));
        const matchesNoComprobante = normalizeText(tramite.no_comprobante || '').includes(normalizeText(searchQuery));
        const matchesActo = normalizeText(tramite.acto || '').includes(normalizeText(searchQuery));
        const matchesItems = (tramite.items || []).some((item) =>
          normalizeText(item.descripcion || '').includes(normalizeText(searchQuery))
        );
        return matchesNoTemporal || matchesNoComprobante || matchesActo || matchesItems;
      });
    }

    return filtered;
  }, [tramites, selectedMatrizador, matrizadorSearch, searchQuery]);

  // Resumen de trámites pendientes por matrizador (para contador y notario)
  const pendingTramitesByMatrizador = useMemo(() => {
    const summary = {};
    const pendingCount = {};
    tramites.forEach((tramite) => {
      if (tramite.semaforo === 'rojo' && tramite.tipo === 'ingreso' && !tramite.comprobante_afectado) {
        const matrizador = normalizeText(tramite.usuario_matrizador || 'Sin Matrizador');
        if (!summary[matrizador]) {
          summary[matrizador] = {
            usuario_matrizador: matrizador,
            tramites_count: 0,
            total: 0,
          };
        }
        summary[matrizador].tramites_count += 1;
        summary[matrizador].total += parseFloat(tramite.total_ajustado || 0) || 0;
        pendingCount[matrizador] = (pendingCount[matrizador] || 0) + 1;
      }
    });
    const summaryArray = Object.values(summary).map((s) => ({
      ...s,
      total: s.total.toFixed(2),
    }));
    return { summary: summaryArray, pendingCount };
  }, [tramites]);

  // Validar permisos
  useEffect(() => {
    if (!loading && (!user || (!user.is_contador && !user.is_notario))) {
      toast.error('No tienes permisos para acceder a esta página.', { position: 'top-right', autoClose: 3000 });
      navigate('/login');
    }
  }, [user, loading, navigate]);

  // Cargar días disponibles
  useEffect(() => {
    const fetchDays = async () => {
      try {
        const response = await api.get('/api/audit/document-days/', {
          params: { page: 1, page_size: 1000 } // Asegurar que obtenemos todos los días
        });
        const fetchedDays = Array.isArray(response.data.days) ? response.data.days : [];
        setDays(fetchedDays);
        if (fetchedDays.length > 0) {
          // Seleccionar automáticamente el día más reciente o el día específico (2025-05-12)
          const targetDay = fetchedDays.find(day => day === '2025-05-12') || fetchedDays[0];
          setSelectedDays([targetDay]);
        }
      } catch (error) {
        toast.error('Error al cargar los días disponibles.', { position: 'top-right', autoClose: 5000 });
        setError('Error al cargar los días disponibles.');
      }
    };
    if (!loading && user && (user.is_contador || user.is_notario)) {
      fetchDays();
    }
  }, [user, loading]);

  // Cargar trámites
  const fetchTramites = useCallback(async () => {
    if (selectedDays.length === 0) {
      toast.error('Por favor, selecciona al menos un día.', { position: 'top-right', autoClose: 3000 });
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      // Cargar trámites para todos los días seleccionados
      const tramitesPromises = selectedDays.map(async (day) => {
        const response = await api.get('/api/audit/list/', {
          params: { 
            fecha_documento: day, 
            page: 1, 
            page_size: 1000, // Cargar todos los trámites de una vez
            justificable: user.is_contador, // Filtrar solo trámites justificables para el contador
            include_pending_days: user.is_notario, // Incluir días pendientes solo para el notario
          },
        });

        if (!response.data || !Array.isArray(response.data.tramites)) {
          throw new Error('Respuesta inválida del servidor: tramites no es un array');
        }

        return response.data;
      });

      const tramitesResponses = await Promise.all(tramitesPromises);

      // Procesar los trámites de todos los días
      let allTramites = [];
      let allPendingDays = [];
      let totalTramitesCount = 0;

      tramitesResponses.forEach((response, index) => {
        const tramitesProcesados = response.tramites.map((tramite) => ({
          ...tramite,
          id: tramite.id || `temp-${Math.random()}`, // Asegurar que cada trámite tenga un ID único
          usuario_matrizador: normalizeText(tramite.usuario_matrizador || 'Sin Matrizador'),
          num_reconocimientos: parseInt(tramite.reconocimientos_count || 0, 10) || 0,
          total: parseFloat(tramite.total || 0) || 0,
          comision_auto: parseFloat(tramite.comision_auto || 0) || 0,
          total_ajustado: parseFloat(tramite.total_ajustado || 0) || 0,
          comprobante_afectado: tramite.comprobante_afectado || '',
          subtotal: parseFloat(tramite.subtotal || 0) || 0,
          iva: parseFloat(tramite.iva || 0) || 0,
          items: Array.isArray(tramite.items) ? tramite.items : [],
          semaforo: tramite.semaforo || 'rojo',
          tipo: tramite.tipo || 'ingreso',
          fecha_comprobante: tramite.fecha_comprobante || '',
          no_temporal: tramite.no_temporal || '',
          no_comprobante: tramite.no_comprobante || '',
          acto: tramite.acto || '',
          fecha_documento: selectedDays[index], // Añadir el día correspondiente
        }));

        allTramites = [...allTramites, ...tramitesProcesados];
        totalTramitesCount += response.total_tramites || tramitesProcesados.length;

        // Acumular días pendientes si el usuario es notario
        if (user.is_notario && response.pending_days) {
          allPendingDays = [...allPendingDays, ...response.pending_days];
        }
      });

      // Actualizar estados
      setTramites(allTramites);
      setFilteredTramites(allTramites);
      setTotalTramites(totalTramitesCount);
      setPendingDays(allPendingDays);

      // Actualizar lista de matrizadores
      const uniqueMatrizadores = [...new Set(allTramites.map((t) => normalizeText(t.usuario_matrizador)))].sort();
      setMatrizadores(uniqueMatrizadores);

    } catch (err) {
      if (err.response) {
        setError(`Error del servidor al cargar los trámites: ${err.response.data.error || 'Error desconocido'}`);
      } else if (err.request) {
        setError('No se pudo conectar al servidor. Verifica tu conexión a internet.');
      } else {
        setError(`Error al cargar los trámites: ${err.message}`);
      }
      toast.error('Error al cargar los trámites.', { position: 'top-right', autoClose: 5000 });
      setTramites([]);
      setFilteredTramites([]);
      setMatrizadores([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedDays, user]);

  // Efecto para recargar trámites cuando cambian los días seleccionados
  useEffect(() => {
    fetchTramites();
  }, [fetchTramites, selectedDays]);

  const handleDayChange = useCallback((e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setSelectedDays(selected);
    setSelectedMatrizador('');
    setTramites([]);
    setFilteredTramites([]);
    setMatrizadores([]);
    setError(null);
    setSearchQuery('');
    setMatrizadorSearch('');
  }, []);

  const handleMatrizadorChange = useCallback((e) => {
    const newMatrizador = e.target.value;
    setSelectedMatrizador(newMatrizador);
  }, []);

  // Debounce para la búsqueda de matrizadores
  const debouncedSetMatrizadorSearch = useCallback(
    debounce((value) => {
      setMatrizadorSearch(value);
    }, 300),
    []
  );

  const handleMatrizadorSearchChange = (e) => {
    debouncedSetMatrizadorSearch(e.target.value);
  };

  // Debounce para la búsqueda avanzada
  const debouncedSetSearchQuery = useCallback(
    debounce((value) => {
      setSearchQuery(value);
    }, 300),
    []
  );

  const handleSearchQueryChange = (e) => {
    debouncedSetSearchQuery(e.target.value);
  };

  // Enviar mensaje por WhatsApp
  const handleWhatsAppClick = (matrizadorName) => {
    const pendingCount = pendingTramitesByMatrizador.pendingCount[matrizadorName] || 0;
    if (pendingCount === 0) {
      toast.info(`No hay trámites pendientes para notificar a ${matrizadorName}.`, {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    const message = user.is_notario
      ? `Hola ${matrizadorName}, soy el notario. Tienes ${pendingCount} trámites pendientes de justificación para el(s) día(s) ${selectedDays.join(', ')}. Por favor, justifícalos lo antes posible.`
      : `Hola ${matrizadorName}, tienes ${pendingCount} trámites pendientes de justificación para el(s) día(s) ${selectedDays.join(', ')}. Por favor, justifícalos lo antes posible.`;
    const encodedMessage = encodeURIComponent(message);

    const normalizedMatrizadorName = normalizeText(matrizadorName);
    const matrizador = matrizadoresData.find((m) => normalizeText(m.nombre) === normalizedMatrizadorName);
    if (matrizador && matrizador.telefono) {
      const whatsappUrl = `https://api.whatsapp.com/send?phone=${matrizador.telefono}&text=${encodedMessage}`;
      window.open(whatsappUrl, '_blank');
    } else {
      toast.error(`No se encontró un número de teléfono para ${matrizadorName}.`, {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Preparar el envío de correos
  const prepareEmailNotification = () => {
    const matrizadoresToNotify = pendingTramitesByMatrizador.summary.filter(
      (summary) => summary.tramites_count > 0
    );
    if (matrizadoresToNotify.length === 0) {
      toast.info('No hay matrizadores con trámites pendientes para notificar.', {
        position: 'top-right',
        autoClose: 3000,
      });
      return;
    }
    setEmailRecipients(matrizadoresToNotify);
    setShowEmailConfirmation(true);
  };

  // Enviar correos a los matrizadores
  const handleEmailClick = async () => {
    setShowEmailConfirmation(false);
    setIsSendingEmails(true);
    try {
      const response = await api.post('/api/audit/notify-matrizadores/', {
        fechas_documento: selectedDays,
      });

      if (response.data.notifications_sent.length > 0) {
        toast.success(`Notificaciones enviadas a ${response.data.notifications_sent.length} matrizadores.`, {
          position: 'top-right',
          autoClose: 5000,
        });
      }
      if (response.data.notifications_failed.length > 0) {
        toast.error(
          `No se pudieron enviar notificaciones a ${response.data.notifications_failed.length} matrizadores: ${response.data.notifications_failed.map(f => `${f.matrizador} (${f.reason})`).join(', ')}`,
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      }
    } catch (err) {
      toast.error('Error al enviar las notificaciones por correo.', {
        position: 'top-right',
        autoClose: 5000,
      });
    } finally {
      setIsSendingEmails(false);
      setEmailRecipients([]);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="min-h-screen flex flex-col text-white p-4 sm:p-6 bg-gray-900"
    >
      <header className="flex flex-wrap justify-between items-center mb-6 sm:mb-8">
        <div className="flex items-center">
          <img src={logo} alt="Logo Notaría 4 Cantón Otavalo" className="w-12 h-auto mr-3 sm:w-16 sm:mr-4" />
          <h1 className="text-2xl sm:text-3xl font-bold">Lista de Trámites</h1>
        </div>
        <Link
          to={user.is_notario ? '/dashboard/notario' : '/dashboard/contador'}
          className="mt-2 sm:mt-0 bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-full hover:bg-notaria-red hover:text-white transition-all"
          aria-label="Volver al dashboard"
        >
          Volver al Dashboard
        </Link>
      </header>

      <main className="flex-1">
        {/* Sección de Días con Trámites Pendientes para el Notario */}
        {pendingDays.length > 0 && user.is_notario && (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
            className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
          >
            <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Días con Trámites Pendientes</h2>
            <div className="space-y-4">
              {pendingDays
                .filter((day) => selectedDays.includes(day.fecha_documento))
                .map((day) => (
                  <div key={day.fecha_documento} className="bg-yellow-500 bg-opacity-20 p-4 rounded-lg">
                    <p className="text-sm sm:text-base">
                      <span className="font-medium text-gray-300">Fecha:</span> {day.fecha_documento}
                    </p>
                    <p className="text-sm sm:text-base">
                      <span className="font-medium text-gray-300">Días de Retraso:</span> {day.days_overdue}
                    </p>
                    <p className="text-sm sm:text-base">
                      <span className="font-medium text-gray-300">Trámites Pendientes:</span> {day.pending_tramites_count}
                    </p>
                  </div>
                ))}
            </div>
          </motion.div>
        )}

        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
        >
          <h2 className="text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">Seleccionar Día(s)</h2>
          <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 items-center">
            <div className="w-full sm:w-1/3">
              <label htmlFor="fecha_documento" className="block text-sm sm:text-lg font-semibold mb-2">
                Día(s) de los Trámites
              </label>
              <select
                id="fecha_documento"
                value={selectedDays}
                onChange={handleDayChange}
                multiple
                className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                aria-label="Seleccionar día(s) de los trámites"
                size={Math.min(days.length, 5)} // Mostrar hasta 5 opciones visibles
              >
                {days.map((day) => (
                  <option key={day} value={day}>
                    {day}
                  </option>
                ))}
              </select>
            </div>
            <motion.button
              onClick={fetchTramites}
              disabled={isLoading || selectedDays.length === 0}
              className={`bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg hover:bg-notaria-red hover:text-white transition-all ${
                isLoading || selectedDays.length === 0 ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              whileHover={{ scale: isLoading || selectedDays.length === 0 ? 1 : 1.02 }}
              whileTap={{ scale: isLoading || selectedDays.length === 0 ? 1 : 0.98 }}
              aria-label="Cargar trámites"
            >
              {isLoading ? (
                <ArrowPathIcon className="w-5 h-5 inline mr-2 animate-spin" />
              ) : (
                'Cargar Trámites'
              )}
            </motion.button>
          </div>
        </motion.div>

        {isLoading ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-200"
          >
            Cargando trámites, por favor espera...
          </motion.div>
        ) : error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-7xl mx-auto bg-red-500 text-white p-4 rounded-lg mb-6 sm:mb-8"
          >
            {error}
          </motion.div>
        ) : selectedDays.length > 0 && filteredTramitesMemo.length > 0 ? (
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="max-w-7xl mx-auto bg-notaria-blue-1 bg-opacity-80 p-4 sm:p-6 rounded-xl shadow-2xl mb-6 sm:mb-8"
          >
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-semibold">Lista de Trámites</h2>
              <div className="w-full sm:w-1/3 mt-2 sm:mt-0">
                <label htmlFor="usuario_matrizador" className="block text-sm sm:text-lg font-semibold mb-2">
                  Seleccionar Matrizador
                </label>
                <select
                  id="usuario_matrizador"
                  value={selectedMatrizador}
                  onChange={handleMatrizadorChange}
                  className="w-full p-2 sm:p-3 rounded-lg bg-notaria-blue-2 text-white focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Seleccionar matrizador"
                >
                  <option value="">Todos los Matrizadores</option>
                  {matrizadores.map((matrizador) => (
                    <option key={matrizador} value={matrizador}>
                      {matrizador}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Sección de Trámites Pendientes por Matrizador para Contador y Notario */}
            {pendingTramitesByMatrizador.summary.length > 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="mb-4 bg-notaria-blue-2 p-4 rounded-lg"
              >
                <p className="text-lg font-semibold">Trámites Pendientes por Matrizador:</p>
                {pendingTramitesByMatrizador.summary.map((summary) => (
                  <div key={summary.usuario_matrizador} className="flex items-center justify-between">
                    <p className="text-lg">
                      {summary.usuario_matrizador}: {summary.tramites_count} trámites, Total: ${summary.total}
                    </p>
                    {(user.is_contador || user.is_notario) && summary.tramites_count > 0 && (
                      <div className="flex space-x-2">
                        {/* Botón de WhatsApp */}
                        <motion.button
                          onClick={() => handleWhatsAppClick(summary.usuario_matrizador)}
                          className="text-green-500 hover:text-green-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          aria-label={`Enviar mensaje de WhatsApp a ${summary.usuario_matrizador} sobre trámites pendientes`}
                          title={`Notificar a ${summary.usuario_matrizador} sobre trámites pendientes vía WhatsApp`}
                        >
                          <ChatBubbleBottomCenterTextIcon className="w-5 h-5" />
                        </motion.button>
                        {/* Botón de Correo (solo para notario) */}
                        {user.is_notario && (
                          <motion.button
                            onClick={prepareEmailNotification}
                            className="text-blue-500 hover:text-blue-400 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500"
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label={`Enviar correo a ${summary.usuario_matrizador} sobre trámites pendientes`}
                            title={`Notificar a ${summary.usuario_matrizador} sobre trámites pendientes vía correo`}
                          >
                            <EnvelopeIcon className="w-5 h-5" />
                          </motion.button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div className="relative w-full">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar matrizador..."
                  onChange={handleMatrizadorSearchChange}
                  className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Buscar matrizador"
                />
              </div>
              <div className="relative w-full">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Buscar por código o descripción..."
                  onChange={handleSearchQueryChange}
                  className="w-full pl-10 p-2 rounded-lg bg-notaria-blue-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-notaria-yellow"
                  aria-label="Buscar por código o descripción"
                />
              </div>
            </div>
            <div className="mb-4 bg-notaria-blue-2 p-4 rounded-lg">
              <p className="text-lg font-semibold">Resumen:</p>
              <p className="text-lg">Total de Trámites: {totalTramites}</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-notaria-blue-2 text-white">
                    <th className="p-3 text-left">No. Temporal</th>
                    <th className="p-3 text-left">No. Comprobante</th>
                    <th className="p-3 text-left">Fecha Comprobante</th>
                    <th className="p-3 text-left">Acto</th>
                    <th className="p-3 text-left">Total Ajustado</th>
                    <th className="p-3 text-left">Comisión Auto</th>
                    <th className="p-3 text-left">Semáforo</th>
                    <th className="p-3 text-left">Tipo</th>
                    <th className="p-3 text-left">Total</th>
                    <th className="p-3 text-left">Comprobante Afectado</th>
                    <th className="p-3 text-left">Matrizador</th>
                    <th className="p-3 text-left">Fecha Documento</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTramitesMemo.length === 0 ? (
                    <tr>
                      <td colSpan="12" className="p-3 text-center text-gray-400">
                        No hay trámites que coincidan con los criterios de búsqueda.
                      </td>
                    </tr>
                  ) : (
                    filteredTramitesMemo.map((tramite) => {
                      const showSemaforo = shouldShowSemaforo(tramite);
                      const tooltip = !showSemaforo
                        ? (tramite.tipo === 'egreso' ? 'Nota de crédito - No justificable' : 'Tiene comprobante afectado - No justificable')
                        : '';
                      return (
                        <motion.tr
                          key={tramite.id}
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.3 }}
                          className={`border-b border-notaria-blue-2 hover:bg-notaria-blue-3 ${
                            !showSemaforo ? 'bg-gray-700 opacity-75' : ''
                          }`}
                        >
                          <td className="p-3">{tramite.no_temporal || '-'}</td>
                          <td className="p-3">{tramite.no_comprobante || '-'}</td>
                          <td className="p-3">{tramite.fecha_comprobante || '-'}</td>
                          <td className="p-3">
                            <div className="max-h-[80px] overflow-y-auto text-sm">
                              {(tramite.items || []).map((item, index) => (
                                <div key={index} className="py-0.5">{item.descripcion || '-'}</div>
                              ))}
                            </div>
                          </td>
                          <td className="p-3">${parseFloat(tramite.total_ajustado || 0).toFixed(2)}</td>
                          <td className="p-3">${parseFloat(tramite.comision_auto || 0).toFixed(2)}</td>
                          <td className="p-3" title={tooltip}>
                            <span
                              className={`inline-block w-4 h-4 rounded-full ${
                                tramite.semaforo === 'rojo'
                                  ? 'bg-red-500'
                                  : tramite.semaforo === 'amarillo'
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                              }`}
                              aria-label={`Estado del semáforo: ${tramite.semaforo}`}
                            ></span>
                          </td>
                          <td className="p-3">{tramite.tipo.charAt(0).toUpperCase() + tramite.tipo.slice(1)}</td>
                          <td className="p-3">${parseFloat(tramite.total || 0).toFixed(2)}</td>
                          <td className="p-3">{tramite.comprobante_afectado || '-'}</td>
                          <td className="p-3">{tramite.usuario_matrizador || '-'}</td>
                          <td className="p-3">{tramite.fecha_documento || '-'}</td>
                        </motion.tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </motion.div>
        ) : selectedDays.length > 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center text-lg text-gray-400"
          >
            No hay trámites disponibles para el(los) día(s) seleccionado(s).
          </motion.div>
        ) : null}
      </main>

      {/* Modal de Confirmación de Envío de Correos */}
      {showEmailConfirmation && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 bg-black bg-opacity-70 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-notaria-blue-1 bg-opacity-95 p-6 rounded-2xl shadow-2xl w-full max-w-md"
          >
            <h3 className="text-xl sm:text-2xl font-semibold mb-4">Confirmar Envío de Correos</h3>
            <p className="mb-4">
              Se enviarán notificaciones por correo a <strong>{emailRecipients.length}</strong> matrizadores con trámites pendientes para el(los) día(s) seleccionado(s): <strong>{selectedDays.join(', ')}</strong>.
            </p>
            <p className="mb-6">¿Estás seguro de que deseas proceder?</p>
            <div className="flex justify-end space-x-4">
              <motion.button
                onClick={() => setShowEmailConfirmation(false)}
                className="bg-gray-500 text-white font-bold py-2 px-4 rounded-lg hover:bg-gray-600 transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Cancelar envío de correos"
              >
                Cancelar
              </motion.button>
              <motion.button
                onClick={handleEmailClick}
                className="bg-notaria-yellow text-notaria-blue-1 font-bold py-2 px-4 rounded-lg hover:bg-notaria-red hover:text-white transition-all"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                aria-label="Confirmar envío de correos"
              >
                Enviar
              </motion.button>
            </div>
          </motion.div>
        </motion.div>
      )}

      <ToastContainer position="top-right" autoClose={3000} pauseOnHover />
    </motion.div>
  );
};

export default TramiteList;